import { decimalUnits, unitsToDecimal, hasPlanCoverage } from './generation-request.js';

const ceilDivide = (numerator, denominator) => numerator / denominator + (numerator % denominator > 0n ? 1n : 0n);

// A review/requote gets its own cache: prompts share the same public base
// estimate, while their reference-edit input text is priced separately.
export function createImageCostEstimator(client, subscription) {
  const cache = new Map();
  const discountBps = hasPlanCoverage(subscription) ? subscription.frontierDiscountBps || 0 : 0;
  return params => estimateExactImageCost(client, params, { cache, discountBps });
}

export async function estimateExactImageCost(client, params, { cache = new Map(), discountBps = 0 } = {}) {
  if (!params.partner) return client.projects.estimateCost(params);
  // SDK 5.60.3 projects away the input-text/vendor fields. Use the same public
  // endpoint once per concrete base configuration to retain those fields.
  const path = [params.tokenType || 'spark', params.network, params.model,
    params.imageCount, params.stepCount, params.previewCount, params.cnEnabled ? 1 : 0,
    params.startingImageStrength ? 1 - params.startingImageStrength : 0,
    params.width, params.height, params.guidance ?? 0,
    params.sampler || '_', params.contextImages ?? 0];
  const query = new URLSearchParams();
  for (const key of ['gptImageQuality', 'outputFormat', 'billingMode']) {
    if (params[key] !== undefined) query.set(key, params[key]);
  }
  const url = `/api/v3/job/estimate/${path.join('/')}?${query}`;
  if (!cache.has(url)) cache.set(url, client.apiClient.socket.get(url));
  const result = await cache.get(url);
  const job = result.quote.job;
  const project = result.quote.project;
  // Socket's reference-edit reservation counts ceil(trim(prompt).length / 4)
  // beyond its quoted text buffer. Derive the unit price, margin and currency
  // conversion from this response; never copy provider prices into the client.
  const promptTokens = params.contextImages > 0 ? Math.ceil((params.prompt || '').trim().length / 4) : 0;
  const extraNumerator = promptTokens > 0 ? BigInt(promptTokens) * decimalUnits(job.inputTextVendorCostUSD) : 0n;
  const extraDenominator = promptTokens > 0 ? BigInt(job.inputTextTokens) * decimalUnits(job.vendorCostUSD) : 1n;
  const multiplier = BigInt(10000 - discountBps);
  function currency(key) {
    const additional = extraNumerator > 0n
      ? ceilDivide(decimalUnits(job[key]) * extraNumerator * BigInt(params.imageCount), extraDenominator) : 0n;
    return unitsToDecimal(ceilDivide((decimalUnits(project[key]) + additional) * multiplier, 10000n));
  }
  return {
    token: currency('costInToken'), usd: currency('costInUSD'),
    spark: currency('costInSpark'), sogni: currency('costInSogni'),
    discountBpsApplied: discountBps,
    estimatedRenderSeconds: result.benchmark?.estimatedRenderTimeSec,
    estimatedTotalSeconds: result.benchmark?.estimatedTotalTimeSec,
    dailyFairUsePct: result.dailyFairUse?.pct
  };
}
